<template>
  <v-panel class="device-edit">
    <template v-slot:header>
      <h2>
        <v-icon name="cpu"></v-icon>
        {{ $t('Device') }}
      </h2>
    </template>

    <template v-slot:default>
      <form>
        <fieldset>
          <v-input v-model="name">
            {{ $t('Name') }}
          </v-input>
          <v-input v-model="vendorId" :class="{ 'is-invalid': !isValidHexOrNumber(vendorId) }">
            {{ $t('Vendor Id') }}
          </v-input>

          <v-input v-model="type.name">
            {{ $t('Type Name') }}
          </v-input>
          <div class="inline">
            <v-input v-model="type.productCode" :class="{ 'is-invalid': !isValidHexOrNumber(type.productCode) }">
              {{ $t('Product Code') }}
            </v-input>
            <v-input v-model="type.revisionNo" :class="{ 'is-invalid': !isValidHexOrNumber(type.revisionNo) }">
              {{ $t('Revision Number') }}
            </v-input>
          </div>
        </fieldset>
      </form>
    </template>

    <template v-slot:footer>
      <ul class="actions flex flex-center">
        <li>
          <button class="success" :disabled="!isValid" @click="save">
            {{ $t('Save') }}
          </button>
        </li>
      </ul>
    </template>
  </v-panel>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useModal } from '@vectioneer/ui'
import { $t } from '/@/i18n'
import { useStore } from '/@/shared/composables/store'
import { Type } from '/@/shared/importers/interfaces'
import { EthercatDevice } from '/@/shared/entities/EthercatDevice'
import HexId from '/@/shared/values/HexId'

interface Props {
  device?: EthercatDevice | null
}

interface Emits {
  (e: 'create-device', device: EthercatDevice): void
}

const props = withDefaults(defineProps<Props>(), {
  device: null,
})

const emit = defineEmits<Emits>()

const { ethercat } = useStore()
const modal = useModal()

const name = ref('')
const id = ref(0)
const vendorId = ref<number | string>(0)
const type = ref<Type>({
  name: '',
  productCode: '',
  revisionNo: '',
})

const isCreating = computed(() => props.device === null)

const isValidHexOrNumber = computed(() => (value: string | number) => {
  if (!value) {
    return true
  }

  try {
    HexId.make(value)
    return true
  } catch (error) {
    return false
  }
})

const isValid = computed(() => {
  const emptyCheck = [name.value, type.value.name, type.value.productCode, type.value.revisionNo]
  const validCheck = [vendorId.value, type.value.productCode, type.value.revisionNo]

  return !emptyCheck.some((val) => val.length === 0) && validCheck.every((val) => isValidHexOrNumber.value(val))
})

const save = () => {
  if (isCreating.value) {
    const device = new EthercatDevice({
      name: name.value,
      id: id.value,
      vendorId: vendorId.value,
      type: type.value,
    })
    emit('create-device', device)
  } else {
    ethercat.updateEntity({
      entity: props.device!,
      updates: {
        name: name.value,
        id: id.value,
        vendorId: vendorId.value,
        type: { ...type.value },
      },
    })
  }

  modal.hide('create-device')
}

onMounted(() => {
  if (!isCreating.value && props.device) {
    name.value = props.device.name
    id.value = props.device.id
    vendorId.value = props.device.vendorId
    type.value = { ...props.device.type }
  } else {
    id.value = ethercat.nextDeviceIndex
  }
})
</script>

<style src="./style.css"></style>
